import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleCheck,
  faCircleXmark,
  faEye,
  faEyeSlash,
  faTrash,
  faRightFromBracket,
} from '@fortawesome/free-solid-svg-icons';
import { microsoftService, MicrosoftTokenResponse } from '../services/microsoftService';
import { credentialService } from '../services/credentialService';
import { authService } from '../services';
import DATA_PROVIDERS, { DataProviderConfig } from '../config/dataProviders';
import Card, { CardBody } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import ConfirmationModal from '../components/common/ConfirmationModal';
import PageTopBar from '../components/ui/PageTopBar';
import { useAuthStore } from '../store';
import { useTheme } from '../hooks';
import { getErrorStatus } from '../utils/errors';
import TagsSection from './settings/TagsSection';

// =============================================================================
// Section keys & sub-nav
// =============================================================================

type SectionKey =
  | 'profile'
  | 'notifications'
  | 'workflow'
  | 'tags'
  | 'data-sources'
  | 'integrations'
  | 'team'
  | 'security';

interface SectionDef {
  key: SectionKey;
  label: string;
  description: string;
  implemented: boolean;
}

const SECTIONS: SectionDef[] = [
  { key: 'profile', label: 'Profile', description: 'Your account information and identity', implemented: true },
  { key: 'notifications', label: 'Notifications', description: 'Email and in-app alert preferences', implemented: false },
  { key: 'workflow', label: 'Workflow Preferences', description: 'Defaults for tasks, autosave, and UI', implemented: false },
  { key: 'tags', label: 'Tags', description: 'Reusable labels for events and other resources', implemented: true },
  { key: 'data-sources', label: 'Data Sources', description: 'External market data feeds', implemented: false },
  { key: 'integrations', label: 'API Integrations', description: 'Provider credentials and connected accounts', implemented: true },
  { key: 'team', label: 'Team Management', description: 'Team members and join requests', implemented: false },
  { key: 'security', label: 'Security', description: 'Password, sessions, and audit log', implemented: false },
];

// =============================================================================
// Provider credential form (used by Integrations section)
// =============================================================================

interface ProviderFormProps {
  config: DataProviderConfig;
  isConfigured: boolean;
  onSave: (providerId: string, values: Record<string, string>) => Promise<void>;
  onDelete: (providerId: string) => void;
  saving: boolean;
}

const ProviderCredentialForm: React.FC<ProviderFormProps> = ({
  config,
  isConfigured,
  onSave,
  onDelete,
  saving,
}) => {
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(config.fields.map(f => [f.key, ''])),
  );
  const [visibleFields, setVisibleFields] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);

  const toggleVisibility = (key: string) => {
    setVisibleFields(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleSave = async () => {
    await onSave(config.id, values);
    setValues(Object.fromEntries(config.fields.map(f => [f.key, ''])));
    setEditing(false);
    setExpanded(false);
  };

  const handleCancelEdit = () => {
    setValues(Object.fromEntries(config.fields.map(f => [f.key, ''])));
    setEditing(false);
  };

  const requiresCredentials = config.requiresCredentials !== false;
  const hasInput = config.fields.some(f => values[f.key]?.trim());
  const showForm = !isConfigured || editing;

  return (
    <div>
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-neutral-50 transition-colors"
      >
        <div className="flex items-center gap-3 min-w-0">
          <h3 className="text-sm font-semibold text-neutral-900 truncate">{config.name}</h3>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <Badge variant={isConfigured ? 'success' : 'secondary'} size="sm">
            {requiresCredentials ? (isConfigured ? 'Configured' : 'Not configured') : 'Available'}
          </Badge>
          <span className={`text-neutral-400 transition-transform ${expanded ? 'rotate-180' : ''}`}>
            &#9662;
          </span>
        </div>
      </button>

      {expanded && (
        <div className="px-5 pb-5 pt-1 space-y-4">
          <p className="text-xs text-neutral-500">{config.description}</p>

          {!requiresCredentials ? (
            <div className="rounded-lg border border-success-200 bg-success-50 px-3 py-3">
              <p className="text-sm font-medium text-success-700">Ready to use</p>
              <p className="text-xs text-success-600 mt-1">
                This provider is built in and does not require credentials. Widgets can use it automatically.
              </p>
            </div>
          ) : isConfigured && !editing ? (
            <>
              <div className="space-y-2">
                {config.fields.map(field => (
                  <div key={field.key} className="flex items-center gap-3">
                    <span className="text-xs font-medium text-neutral-500 w-24 shrink-0">{field.label}</span>
                    <span className="text-sm text-neutral-400 tracking-widest">{'••••••••'}</span>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2 pt-1">
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={() => onDelete(config.id)}
                  className="text-danger-500 hover:text-danger-700"
                  icon={<FontAwesomeIcon icon={faTrash} />}
                >
                  Remove
                </Button>
                <div className="flex-1" />
                <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>
                  Update Credentials
                </Button>
              </div>
            </>
          ) : showForm ? (
            <>
              <div className="space-y-3">
                {config.fields.map(field => {
                  const isSecret = field.secret !== false;
                  const isVisible = !isSecret || visibleFields.has(field.key);
                  return (
                    <div key={field.key}>
                      <label className="block text-xs font-medium text-neutral-700 mb-1">
                        {field.label}
                      </label>
                      <div className="relative">
                        <input
                          type={isVisible ? 'text' : 'password'}
                          placeholder={field.placeholder || ''}
                          value={values[field.key] || ''}
                          onChange={e =>
                            setValues(prev => ({ ...prev, [field.key]: e.target.value }))
                          }
                          className={`w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm
                                     bg-surface text-neutral-900 placeholder-neutral-400
                                     ${isSecret ? 'pr-10' : ''}`}
                        />
                        {isSecret && (
                          <button
                            type="button"
                            onClick={() => toggleVisibility(field.key)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                          >
                            <FontAwesomeIcon icon={visibleFields.has(field.key) ? faEyeSlash : faEye} size="sm" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="flex items-center gap-2 pt-1">
                {isConfigured && (
                  <Button variant="ghost" size="xs" onClick={handleCancelEdit}>
                    Cancel
                  </Button>
                )}
                <div className="flex-1" />
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleSave}
                  disabled={saving || !hasInput}
                >
                  {saving ? 'Saving...' : 'Save Credentials'}
                </Button>
              </div>
            </>
          ) : null}
        </div>
      )}
    </div>
  );
};

// =============================================================================
// Reusable section header
// =============================================================================

interface SectionHeaderProps {
  title: string;
  description: string;
}

const SectionHeader: React.FC<SectionHeaderProps> = ({ title, description }) => (
  <div className="mb-6">
    <h2 className="text-2xl font-semibold text-neutral-900">{title}</h2>
    <p className="text-sm text-neutral-500 mt-1">{description}</p>
  </div>
);

// =============================================================================
// Placeholder section — for areas we haven't built yet
// =============================================================================

interface PlaceholderSectionProps {
  title: string;
  description: string;
  hint: string;
}

const PlaceholderSection: React.FC<PlaceholderSectionProps> = ({ title, description, hint }) => (
  <div>
    <SectionHeader title={title} description={description} />
    <Card variant="default">
      <CardBody>
        <div className="py-16 flex flex-col items-center text-center">
          <Badge variant="secondary" size="md">Coming soon</Badge>
          <p className="text-sm text-neutral-500 mt-3 max-w-md">{hint}</p>
        </div>
      </CardBody>
    </Card>
  </div>
);

// =============================================================================
// Shared building blocks
// =============================================================================

const roleVariants: Record<string, 'primary' | 'secondary' | 'success'> = {
  admin: 'primary',
  owner: 'secondary',
  worker: 'success',
};

interface ReadOnlyFieldProps {
  label: string;
  value: string | null | undefined;
  placeholder?: string;
}

const ReadOnlyField: React.FC<ReadOnlyFieldProps> = ({ label, value, placeholder }) => (
  <div>
    <label className="block text-[11px] font-semibold text-neutral-500 uppercase tracking-wider mb-1.5">
      {label}
    </label>
    <div className="w-full h-10 px-3 flex items-center border border-neutral-200 rounded-lg text-sm bg-neutral-50 text-neutral-700">
      {value || <span className="text-neutral-400">{placeholder ?? 'Not set'}</span>}
    </div>
  </div>
);

interface ToggleRowProps {
  label: string;
  description: string;
  checked: boolean;
  onChange?: (next: boolean) => void;
  disabled?: boolean;
  badge?: string;
}

const ToggleRow: React.FC<ToggleRowProps> = ({
  label,
  description,
  checked,
  onChange,
  disabled = false,
  badge,
}) => (
  <div
    className={`flex items-center justify-between gap-6 py-4 ${
      disabled ? 'opacity-60' : ''
    }`}
  >
    <div className="min-w-0">
      <div className="flex items-center gap-2">
        <p className="text-sm font-medium text-neutral-900">{label}</p>
        {badge && (
          <span className="text-[9px] uppercase tracking-wider text-neutral-400 font-semibold">
            {badge}
          </span>
        )}
      </div>
      <p className="text-xs text-neutral-500 mt-0.5">{description}</p>
    </div>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange?.(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
        checked ? 'bg-success-500' : 'bg-neutral-300'
      } ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <span
        className={`inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform ${
          checked ? 'translate-x-5' : 'translate-x-0.5'
        }`}
      />
    </button>
  </div>
);

// =============================================================================
// Profile section
// =============================================================================

const ProfileSection: React.FC = () => {
  const { user } = useAuthStore();
  const { theme, toggleTheme } = useTheme();

  const initials = (user?.fullName || user?.email || '?')
    .split(' ')
    .map(s => s.trim()[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <div>
      <SectionHeader title="Profile" description="Your account information and identity" />

      {/* Identity card */}
      <div className="mb-8 p-5 rounded-xl border border-neutral-200 bg-surface flex items-center gap-4">
        <div className="w-16 h-16 rounded-full bg-primary-600 text-white flex items-center justify-center text-lg font-semibold shrink-0">
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold text-neutral-900 truncate">
              {user?.fullName || 'Unnamed user'}
            </h3>
            {user?.role && (
              <Badge variant={roleVariants[user.role] || 'secondary'} size="sm">
                {user.role}
              </Badge>
            )}
          </div>
          <p className="text-xs text-neutral-500 truncate mt-0.5">{user?.email}</p>
          {user?.createdAt && (
            <p className="text-[11px] text-neutral-400 mt-1">
              Joined {new Date(user.createdAt).toLocaleDateString()}
            </p>
          )}
        </div>
        <Button variant="outline" size="sm" disabled title="Coming soon">
          Change photo
        </Button>
      </div>

      {/* Account fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-5 mb-2">
        <ReadOnlyField label="Full name" value={user?.fullName} />
        <ReadOnlyField label="Role" value={user?.role} />
        <ReadOnlyField label="Email" value={user?.email} />
        <ReadOnlyField label="User ID" value={user?.id} />
      </div>
      <p className="text-xs text-neutral-400 mb-10">
        Profile editing is not yet available — contact your administrator to update these fields.
      </p>

      {/* Preferences */}
      <h3 className="text-lg font-semibold text-neutral-900 mb-1">Preferences</h3>
      <p className="text-sm text-neutral-500 mb-2">
        Personal settings that apply across the application
      </p>
      <div className="divide-y divide-neutral-200 border-t border-neutral-200">
        <ToggleRow
          label="Dark mode"
          description="Use dark theme across the application"
          checked={theme === 'dark'}
          onChange={toggleTheme}
        />
        <ToggleRow
          label="Email notifications"
          description="Receive emails for assignments, deadlines, and workflow updates"
          checked={false}
          disabled
          badge="Soon"
        />
        <ToggleRow
          label="Auto-save drafts"
          description="Automatically save workflow progress every 30 seconds"
          checked
          disabled
          badge="Soon"
        />
      </div>
    </div>
  );
};

// =============================================================================
// Integrations section (data provider credentials + Microsoft 365)
// =============================================================================

interface IntegrationsSectionProps {
  microsoftStatus: MicrosoftTokenResponse | null;
  isLoading: boolean;
  isConnecting: boolean;
  isDisconnecting: boolean;
  configuredProviders: Set<string>;
  credentialsLoading: boolean;
  savingProvider: string | null;
  onConnectMicrosoft: () => void;
  onDisconnectMicrosoft: () => void;
  onSaveProvider: (providerId: string, values: Record<string, string>) => Promise<void>;
  onDeleteProvider: (providerId: string) => void;
}

const IntegrationsSection: React.FC<IntegrationsSectionProps> = ({
  microsoftStatus,
  isLoading,
  isConnecting,
  isDisconnecting,
  configuredProviders,
  credentialsLoading,
  savingProvider,
  onConnectMicrosoft,
  onDisconnectMicrosoft,
  onSaveProvider,
  onDeleteProvider,
}) => {
  const [microsoftExpanded, setMicrosoftExpanded] = useState(false);

  return (
    <div>
      <SectionHeader
        title="API Integrations"
        description="API keys and connected accounts. Stored securely on the backend and used by the data provider service."
      />

      {credentialsLoading ? (
        <div className="flex items-center gap-2 text-neutral-500 py-6">
          <div className="animate-spin rounded-full h-4 w-4 border-2 border-neutral-300 border-t-primary-600" />
          <span className="text-sm">Loading credentials...</span>
        </div>
      ) : (
        <div className="border border-neutral-200 rounded-xl bg-surface overflow-hidden divide-y divide-neutral-200">
          {DATA_PROVIDERS.map(provider => (
            <ProviderCredentialForm
              key={provider.id}
              config={provider}
              isConfigured={configuredProviders.has(provider.id)}
              onSave={onSaveProvider}
              onDelete={onDeleteProvider}
              saving={savingProvider === provider.id}
            />
          ))}

          {/* Microsoft 365 */}
          <div>
            <button
              type="button"
              onClick={() => setMicrosoftExpanded(v => !v)}
              className="w-full flex items-center justify-between px-5 py-4 hover:bg-neutral-50 transition-colors"
            >
              <h3 className="text-sm font-semibold text-neutral-900">Microsoft 365</h3>
              <div className="flex items-center gap-3">
                {!isLoading && (
                  <Badge variant={microsoftStatus?.connected ? 'success' : 'secondary'} size="sm">
                    {microsoftStatus?.connected ? 'Connected' : 'Not connected'}
                  </Badge>
                )}
                <span className={`text-neutral-400 transition-transform ${microsoftExpanded ? 'rotate-180' : ''}`}>
                  &#9662;
                </span>
              </div>
            </button>

            {microsoftExpanded && (
              <div className="px-5 pb-5 pt-1 space-y-4">
                <p className="text-xs text-neutral-500">
                  Connect your Microsoft account to use Excel and Outlook widgets (OneDrive files, email compose, spreadsheets).
                </p>

                {isLoading ? (
                  <div className="flex items-center gap-2 text-neutral-500">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-neutral-300 border-t-primary-600" />
                    <span className="text-sm">Checking connection status...</span>
                  </div>
                ) : microsoftStatus?.connected ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-success-600">
                      <span className="text-sm font-medium">Connected</span>
                      {microsoftStatus.microsoftEmail && (
                        <span className="text-sm text-neutral-500">
                          ({microsoftStatus.microsoftEmail})
                        </span>
                      )}
                    </div>
                    {microsoftStatus.expiresAt && (
                      <p className="text-xs text-neutral-500">
                        Token expires: {new Date(microsoftStatus.expiresAt).toLocaleDateString()}
                      </p>
                    )}
                    <div className="flex items-center gap-2 pt-1">
                      <Button
                        variant="ghost"
                        size="xs"
                        onClick={onDisconnectMicrosoft}
                        disabled={isDisconnecting}
                        className="text-danger-500 hover:text-danger-700"
                      >
                        {isDisconnecting ? 'Disconnecting...' : 'Remove'}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={onConnectMicrosoft}
                    disabled={isConnecting}
                  >
                    {isConnecting ? 'Connecting...' : 'Connect Microsoft Account'}
                  </Button>
                )}

                <details className="text-xs">
                  <summary className="text-neutral-500 hover:text-primary-600 cursor-pointer font-medium transition-colors">
                    Learn More
                  </summary>
                  <div className="mt-2 space-y-1.5 text-neutral-500">
                    <p>This application requests the following permissions:</p>
                    <ul className="space-y-1 ml-3">
                      <li><strong>Files.Read</strong> — Read your OneDrive files</li>
                      <li><strong>Mail.Send</strong> — Send emails on your behalf</li>
                      <li><strong>Mail.ReadWrite</strong> — Create email drafts</li>
                      <li><strong>User.Read</strong> — Read your basic profile</li>
                    </ul>
                    <p>
                      You can revoke access at any time from your{' '}
                      <a
                        href="https://myaccount.microsoft.com/permissions"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary-600 underline hover:no-underline"
                      >
                        Microsoft account permissions page
                      </a>.
                    </p>
                  </div>
                </details>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// =============================================================================
// Team section — links out to existing /team page
// =============================================================================

const TeamSection: React.FC = () => (
  <div>
    <SectionHeader title="Team Management" description="Members, roles, and join requests" />
    <Card variant="default">
      <CardBody>
        <p className="text-sm text-neutral-600 mb-4">
          Team management lives on its own page where you can invite members, approve join
          requests, and assign coverage.
        </p>
        <Link to="/team">
          <Button variant="primary" size="sm">Open Team page</Button>
        </Link>
      </CardBody>
    </Card>
  </div>
);

// =============================================================================
// Settings page shell
// =============================================================================

const Settings: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAuthStore();
  const [activeSection, setActiveSection] = useState<SectionKey>('profile');

  const handleLogout = async () => {
    try {
      await authService.logout();
    } catch {
      // Best-effort — proceed with local logout regardless
    } finally {
      logout();
      navigate('/login');
    }
  };

  // ── State shared by Integrations ──
  const [microsoftStatus, setMicrosoftStatus] = useState<MicrosoftTokenResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);

  const [configuredProviders, setConfiguredProviders] = useState<Set<string>>(new Set());
  const [credentialsLoading, setCredentialsLoading] = useState(true);
  const [savingProvider, setSavingProvider] = useState<string | null>(null);
  const [showDeleteProviderConfirm, setShowDeleteProviderConfirm] = useState<string | null>(null);

  const loadCredentials = useCallback(async () => {
    try {
      setCredentialsLoading(true);
      const providers = await credentialService.list();
      setConfiguredProviders(new Set(providers.map(p => p.provider)));
    } catch (err) {
      console.error('Failed to load credentials:', err);
    } finally {
      setCredentialsLoading(false);
    }
  }, []);

  const handleSaveProvider = async (providerId: string, values: Record<string, string>) => {
    const entries = Object.entries(values).filter(([, v]) => v.trim());
    if (entries.length === 0) return;
    try {
      setSavingProvider(providerId);
      setError(null);
      await credentialService.set({
        provider: providerId,
        credentials: entries.map(([key, value]) => ({ key, value: value.trim() })),
      });
      const providerName = DATA_PROVIDERS.find(p => p.id === providerId)?.name ?? providerId;
      setSuccessMessage(`Credentials for ${providerName} saved successfully.`);
      setTimeout(() => setSuccessMessage(null), 3000);
      await loadCredentials();
    } catch {
      setError('Failed to save credentials.');
    } finally {
      setSavingProvider(null);
    }
  };

  const handleDeleteProvider = async (providerId: string) => {
    try {
      setError(null);
      await credentialService.deleteProvider(providerId);
      const providerName = DATA_PROVIDERS.find(p => p.id === providerId)?.name ?? providerId;
      setSuccessMessage(`Credentials for ${providerName} deleted.`);
      setTimeout(() => setSuccessMessage(null), 3000);
      await loadCredentials();
    } catch {
      setError('Failed to delete credentials.');
    } finally {
      setShowDeleteProviderConfirm(null);
    }
  };

  const loadMicrosoftStatus = async () => {
    try {
      setIsLoading(true);
      const status = await microsoftService.getConnectionStatus();
      setMicrosoftStatus(status);
    } catch (err: unknown) {
      if (getErrorStatus(err) !== 404) {
        console.error('Failed to load Microsoft status:', err);
      }
      setMicrosoftStatus({ connected: false });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadMicrosoftStatus();
    loadCredentials();
  }, [loadCredentials]);

  const handleConnectMicrosoft = async () => {
    try {
      setIsConnecting(true);
      setError(null);
      const { authUrl, codeVerifier, clientId, redirectUri, scope } =
        await microsoftService.getAuthUrl();
      sessionStorage.setItem('microsoft_auth_return', '/settings');
      sessionStorage.setItem('microsoft_code_verifier', codeVerifier);
      sessionStorage.setItem('microsoft_client_id', clientId);
      sessionStorage.setItem('microsoft_redirect_uri', redirectUri);
      sessionStorage.setItem('microsoft_scope', scope);
      window.location.href = authUrl;
    } catch {
      setError('Failed to start Microsoft authentication. Please try again.');
      setIsConnecting(false);
    }
  };

  const confirmDisconnectMicrosoft = async () => {
    try {
      setIsDisconnecting(true);
      setError(null);
      await microsoftService.disconnect();
      setMicrosoftStatus({ connected: false });
      setSuccessMessage('Microsoft account disconnected successfully.');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch {
      setError('Failed to disconnect Microsoft account. Please try again.');
    } finally {
      setIsDisconnecting(false);
      setShowDisconnectConfirm(false);
    }
  };

  const renderSection = () => {
    switch (activeSection) {
      case 'profile':
        return <ProfileSection />;
      case 'integrations':
        return (
          <IntegrationsSection
            microsoftStatus={microsoftStatus}
            isLoading={isLoading}
            isConnecting={isConnecting}
            isDisconnecting={isDisconnecting}
            configuredProviders={configuredProviders}
            credentialsLoading={credentialsLoading}
            savingProvider={savingProvider}
            onConnectMicrosoft={handleConnectMicrosoft}
            onDisconnectMicrosoft={() => setShowDisconnectConfirm(true)}
            onSaveProvider={handleSaveProvider}
            onDeleteProvider={id => setShowDeleteProviderConfirm(id)}
          />
        );
      case 'team':
        return <TeamSection />;
      case 'tags':
        return <TagsSection />;
      case 'notifications':
        return (
          <PlaceholderSection
            title="Notifications"
            description="Email and in-app alert preferences"
            hint="Pick which events trigger an email or in-app notification — assignments, comments, deadline reminders, and system alerts."
          />
        );
      case 'workflow':
        return (
          <PlaceholderSection
            title="Workflow Preferences"
            description="Defaults for tasks, autosave, and the builder UI"
            hint="Set autosave intervals, default views, keyboard shortcut overrides, and other per-user workflow defaults."
          />
        );
      case 'data-sources':
        return (
          <PlaceholderSection
            title="Data Sources"
            description="External market data feeds and refresh policy"
            hint="Pick which market data feeds widgets pull from by default and how often they refresh. Credentials live under API Integrations."
          />
        );
      case 'security':
        return (
          <PlaceholderSection
            title="Security"
            description="Password, sessions, and audit log"
            hint="Change your password, review active sessions on other devices, and view recent account activity."
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="h-full flex flex-col bg-neutral-50">
      <PageTopBar title="Settings" description="Manage your account, integrations, and preferences" />

      <div className="flex-1 min-h-0 flex overflow-hidden">
        {/* Sub-nav */}
        <aside className="w-60 shrink-0 bg-surface border-r border-neutral-200 flex flex-col">
          <nav className="flex-1 overflow-y-auto py-4">
            {SECTIONS.map(section => {
              const isActive = activeSection === section.key;
              return (
                <button
                  key={section.key}
                  type="button"
                  onClick={() => setActiveSection(section.key)}
                  className={`w-full text-left px-5 py-2 text-sm transition-colors flex items-center justify-between gap-2 ${
                    isActive
                      ? 'bg-primary-50 text-primary-700 font-medium border-l-2 border-primary-600'
                      : 'text-neutral-600 hover:text-neutral-900 hover:bg-neutral-50 border-l-2 border-transparent'
                  }`}
                >
                  <span className="truncate">{section.label}</span>
                  {!section.implemented && (
                    <span className="text-[9px] uppercase tracking-wider text-neutral-400 font-medium">
                      Soon
                    </span>
                  )}
                </button>
              );
            })}
          </nav>

          {/* Sign out — pinned to the bottom of the sub-nav */}
          <div className="shrink-0 border-t border-neutral-200 py-3 px-3">
            <button
              type="button"
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-danger-600 hover:bg-danger-50 transition-colors"
            >
              <FontAwesomeIcon icon={faRightFromBracket} />
              <span>Sign out</span>
            </button>
          </div>
        </aside>

        {/* Section content */}
        <div className="flex-1 min-w-0 overflow-y-auto">
          <div className="px-10 py-8">
            {/* Status messages */}
            {successMessage && (
              <div className="mb-6 p-4 bg-success-50 border border-success-200 rounded-xl flex items-center gap-3">
                <div className="w-8 h-8 bg-success-100 rounded-lg flex items-center justify-center text-success-600">
                  <FontAwesomeIcon icon={faCircleCheck} />
                </div>
                <p className="text-sm text-success-700">{successMessage}</p>
              </div>
            )}

            {error && (
              <div className="mb-6 p-4 bg-danger-50 border border-danger-200 rounded-xl flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-danger-100 rounded-lg flex items-center justify-center text-danger-600">
                    <FontAwesomeIcon icon={faCircleXmark} />
                  </div>
                  <p className="text-sm text-danger-700">{error}</p>
                </div>
                <button
                  onClick={() => setError(null)}
                  className="text-danger-500 hover:text-danger-700 text-sm font-medium"
                >
                  Dismiss
                </button>
              </div>
            )}

            {renderSection()}
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={showDisconnectConfirm}
        onClose={() => setShowDisconnectConfirm(false)}
        onAction={confirmDisconnectMicrosoft}
        title="Disconnect Microsoft Account"
        description="Are you sure you want to disconnect your Microsoft account? You will need to reconnect to use Excel and Outlook widgets."
        actionText="Disconnect"
      />

      <ConfirmationModal
        isOpen={!!showDeleteProviderConfirm}
        onClose={() => setShowDeleteProviderConfirm(null)}
        onAction={() => showDeleteProviderConfirm && handleDeleteProvider(showDeleteProviderConfirm)}
        title="Delete Provider Credentials"
        description={`Are you sure you want to delete all credentials for "${
          DATA_PROVIDERS.find(p => p.id === showDeleteProviderConfirm)?.name ?? showDeleteProviderConfirm
        }"? This action cannot be undone.`}
        actionText="Delete"
      />
    </div>
  );
};

export default Settings;
